import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoScript from '@/components/base/SeoScript';

export default function About() {
  const faqs = [
    { q: '韩漫画网的漫画资源是否全部免费？', a: '是的，韩漫画网所有漫画均可免费阅读。我们致力于为韩漫爱好者提供无门槛的阅读体验，无需付费即可畅读全部作品。' },
    { q: '下拉式阅读是什么意思？', a: '下拉式阅读是指在同一个页面上通过向下滚动鼠标或滑动屏幕来连续阅读，无需翻页或跳转，提供无遮挡、连贯流畅的阅读体验。' },
    { q: '漫画更新频率如何？', a: '热门漫画通常每日更新一话，部分作品可能每周更新2-3话。具体更新频率取决于原作连载进度，我们的翻译团队会第一时间跟进。' },
    { q: '支持哪些设备阅读？', a: '韩漫画网采用响应式设计，全面支持电脑、平板和手机端阅读。无论在哪个设备上，都能享受流畅的下拉式阅读体验。' },
    { q: '如何收藏喜欢的漫画？', a: '注册登录后，您可以点击漫画详情页的收藏按钮将作品加入书架，方便随时追踪更新进度。' },
    { q: '遇到阅读问题如何联系客服？', a: '您可以通过页面底部的联系方式发送邮件至 hello@zzxcdell.com，或在工作时间拨打客服电话 400-888-6666，我们的客服团队会尽快为您解决。' },
  ];

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://zzxcdell.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://zzxcdell.com/about' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于韩漫画网 - 韩漫大全下拉式免费阅读平台',
    description: '了解韩漫画网的品牌故事、平台数据与发展历程。专注韩漫在线阅读，打造极致下拉式阅读体验。收录1200+部韩漫作品，日活跃用户50万+。',
    url: 'http://zzxcdell.com/about',
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    document.title = '关于我们 - 韩漫画网 | 韩漫大全下拉式免费阅读平台';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', '了解韩漫画网的品牌故事、平台数据与发展历程。专注韩漫在线阅读，打造极致下拉式阅读体验。收录1200+部韩漫作品，日活跃用户50万+。');
    }
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) {
      metaKw.setAttribute('content', '关于我们,韩漫画网,韩漫平台,漫画阅读,下拉式免费,无遮挡韩漫');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'http://zzxcdell.com/about');
    }
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <SeoScript data={breadcrumbList} />
      <SeoScript data={faqSchema} />
      <SeoScript data={webPageSchema} />
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 px-6 max-w-screen-xl mx-auto">
        <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs">
          <Link to="/" className="text-foreground-500 hover:text-foreground-200 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-600"></i>
          <span className="text-foreground-300">关于我们</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20Korean%20manhwa%20studio%20scene%2C%20drawing%20table%20with%20comic%20panels%2C%20warm%20ambient%20light%2C%20creative%20workspace%2C%20dark%20red%20and%20black%20atmosphere%2C%20professional%20illustration%20studio%2C%20cinematic%20composition&width=1600&height=700&seq=about-hero-2026&orientation=landscape"
          alt="韩漫画网"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-foreground-50 mb-4">
            关于韩漫画网
          </h1>
          <p className="text-foreground-300 text-sm md:text-base max-w-xl leading-relaxed">
            专注韩漫在线阅读，打造极致下拉式阅读体验
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-16 md:py-24 px-6 max-w-screen-xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="lg:w-1/2">
            <div className="relative rounded-xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20illustration%20studio%20with%20artists%20working%2C%20creative%20team%20collaboration%2C%20warm%20lighting%2C%20drawing%20tablets%20and%20sketches%2C%20professional%20creative%20environment%2C%20dark%20red%20and%20black%20atmosphere&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                alt="品牌故事"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-primary-500/30 backdrop-blur-sm text-xs font-medium text-foreground-50 whitespace-nowrap">品牌故事</span>
                <span className="px-3 py-1 rounded-full bg-background-50/30 backdrop-blur-sm text-xs font-medium text-foreground-50 whitespace-nowrap">核心团队</span>
                <span className="px-3 py-1 rounded-full bg-background-50/30 backdrop-blur-sm text-xs font-medium text-foreground-50 whitespace-nowrap">发展历程</span>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <span className="px-4 py-1.5 rounded-full border border-primary-500/40 text-primary-400 text-xs font-medium whitespace-nowrap">关于我们</span>
              <span className="px-4 py-1.5 rounded-full text-foreground-500 text-xs font-medium hover:text-foreground-200 cursor-pointer whitespace-nowrap">联系方式</span>
              <span className="px-4 py-1.5 rounded-full text-foreground-500 text-xs font-medium hover:text-foreground-200 cursor-pointer whitespace-nowrap">加入我们</span>
            </div>

            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4">
                我们的故事
              </h2>
              <p className="text-sm text-foreground-300 leading-relaxed mb-4">
                韩漫画网成立于2020年，由一群热爱韩国漫画的年轻人创办。我们深知读者对于流畅、无干扰阅读体验的渴望，因此致力于打造一个专注于<strong>韩漫下拉式在线阅读</strong>的平台。
              </p>
              <p className="text-sm text-foreground-300 leading-relaxed mb-4">
                经过多年的积累与发展，韩漫画网已收录超过<strong className="text-primary-400">1200部</strong>韩漫作品，涵盖动作热血、浪漫恋爱、奇幻冒险、悬疑惊悚、搞笑日常等多个类别。我们拥有专业的翻译和校对团队，确保每一部作品的翻译质量。
              </p>
              <p className="text-sm text-foreground-300 leading-relaxed">
                我们的使命是让每一位韩漫爱好者都能享受到<strong>无遮挡、下拉式、快速加载</strong>的极致阅读体验，让漫画情节成为唯一的焦点。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-6 max-w-screen-xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { label: '收录漫画', value: '1,200+', icon: 'ri-book-open-line' },
            { label: '已完结作品', value: '350+', icon: 'ri-check-double-line' },
            { label: '日活跃用户', value: '50万+', icon: 'ri-user-line' },
            { label: '总阅读次数', value: '8.6亿+', icon: 'ri-eye-line' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-500/10 flex items-center justify-center">
                <i className={`${stat.icon} text-primary-400 text-lg`}></i>
              </div>
              <p className="font-heading text-2xl md:text-3xl font-black text-foreground-50 mb-1">{stat.value}</p>
              <p className="text-xs text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Business Cooperation */}
      <section className="py-16 md:py-24 px-6 bg-background-100">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-500/10 border border-secondary-500/20 text-secondary-400 text-xs font-medium mb-4">
              <i className="ri-briefcase-line text-xs"></i>
              商务合作
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50">
              合作共赢，共创未来
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-12">
            {[
              { icon: 'ri-advertisement-line', title: '广告投放', desc: '首页轮播广告位、分类页横幅广告、漫画内嵌广告等多种形式，精准触达韩漫读者群体。' },
              { icon: 'ri-translate', title: '版权合作', desc: '欢迎韩国漫画版权方洽谈合作，我们拥有专业翻译团队和成熟分发渠道，助力作品在华语市场推广。' },
              { icon: 'ri-links-line', title: '内容联运', desc: '与动漫社区、二次元平台等开展内容联动合作，共同扩大韩漫文化在华影响力。' },
            ].map((item, idx) => (
              <div key={idx} className="bg-background-200 rounded-lg p-6 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-secondary-500/10 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-secondary-400 text-xl`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-100 mb-3">{item.title}</h3>
                <p className="text-xs text-foreground-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-background-200 rounded-xl p-6 md:p-8 max-w-2xl mx-auto">
            <h3 className="font-heading text-lg font-bold text-foreground-50 mb-4 text-center">合作联系方式</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-foreground-500 mb-1">商务合作邮箱</p>
                <p className="text-sm text-foreground-200 font-semibold">hello@zzxcdell.com</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">版权合作邮箱</p>
                <p className="text-sm text-foreground-200 font-semibold">hello@zzxcdell.com</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">商务电话</p>
                <p className="text-sm text-foreground-200 font-semibold">400-888-6666 转 2</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-1">工作时间</p>
                <p className="text-sm text-foreground-200 font-semibold">周一至周五 9:00 - 18:00</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-background-300/30 text-center">
              <p className="text-sm text-foreground-300 font-semibold">公司地址</p>
              <p className="text-xs text-foreground-500 mt-1">上海市浦东新区张江高科技园区博云路2号浦软大厦18层</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/10 border border-accent-500/20 text-accent-400 text-xs font-medium mb-4">
              <i className="ri-shield-star-line text-xs"></i>
              合作伙伴与口碑
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50">
              业界认可与读者信赖
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {[
              { media: '动漫资讯网', quote: '韩漫画网是中文互联网上韩漫资源最丰富、阅读体验最优的平台之一。下拉式阅读设计堪称行业标杆。', rating: 5 },
              { media: '二次元观察', quote: '翻译质量高，更新速度快。从动作热血到浪漫恋爱，分类清晰，找漫画非常方便。强烈推荐给所有韩漫爱好者。', rating: 5 },
              { media: '数字阅读周刊', quote: '无广告干扰的纯净阅读环境，加上下拉式无遮挡设计，真正做到了让读者专注于内容本身。', rating: 5 },
            ].map((item, idx) => (
              <div key={idx} className="bg-background-100 rounded-lg p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center">
                    <i className="ri-double-quotes-l text-accent-400 text-sm"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-100">{item.media}</p>
                </div>
                <p className="text-sm text-foreground-300 leading-relaxed flex-1">
                  &ldquo;{item.quote}&rdquo;
                </p>
                <div className="flex items-center gap-1 mt-4">
                  {[...Array(item.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 px-6 max-w-screen-xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50">
            常见问题
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, idx) => (
            <details key={idx} className="group bg-background-100 rounded-lg overflow-hidden">
              <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                <h3 className="text-sm font-semibold text-foreground-100 pr-4">{faq.q}</h3>
                <i className="ri-add-line text-foreground-400 group-open:hidden text-lg flex-shrink-0"></i>
                <i className="ri-subtract-line text-foreground-400 hidden group-open:block text-lg flex-shrink-0"></i>
              </summary>
              <div className="px-5 pb-5">
                <p className="text-sm text-foreground-400 leading-relaxed">{faq.a}</p>
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="relative py-24 md:py-36 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20comic%20studio%20door%20with%20light%20streaming%20through%2C%20creative%20workspace%20entrance%2C%20inviting%20and%20mysterious%2C%20dark%20red%20and%20black%20tones%2C%20cinematic%20lighting%2C%20professional%20illustration%20studio%20atmosphere&width=1600&height=700&seq=about-cta-2026&orientation=landscape"
          alt="联系我们"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/50 to-background-50/90" />

        <div className="relative z-10 max-w-screen-xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground-50 tracking-wide mb-4">
            期待与您联系
          </h2>
          <p className="text-foreground-300 text-sm md:text-base max-w-xl mx-auto leading-relaxed mb-4">
            有任何建议、合作意向或阅读问题，欢迎随时联系我们
          </p>
          <p className="text-foreground-400 text-sm max-w-xl mx-auto leading-relaxed mb-10">
            客服邮箱：hello@zzxcdell.com · 客服电话：400-888-6666
          </p>

          <a
            href="mailto:hello@zzxcdell.com"
            className="inline-flex items-center gap-3 px-8 py-4 bg-background-950 text-foreground-50 rounded-full text-sm font-semibold hover:bg-background-900 transition-all whitespace-nowrap cursor-pointer"
          >
            <span className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-mail-line text-background-50 text-sm"></i>
            </span>
            发送邮件
            <i className="ri-arrow-right-up-line text-lg"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}